import { Suspense, lazy, useEffect, useState, type CSSProperties } from "react";
import { LocationMap } from "./near-view-map";
import { preload } from "react-dom";
import { useTranslation } from "react-i18next";
import { imageSrcSet } from "@majourneys/shared";
import { PEWTER_GAP, pewterFrame } from "@majourneys/shared/ui";
import { Icon } from "@majourneys/icons";
import { ImageWithFallback } from "../internal-ui/image-with-fallback";
import { PW } from "../internal-ui/pewter-shell";
import { PhoneGallery, type PhoneGalleryAction } from "./phone-gallery";
import type { VideoModalCta } from "../internal-ui/video-modal";
import { DirectionsLink } from "./location-card";

const Lightbox = lazy(() =>
  import("../internal-ui/lightbox").then((m) => ({ default: m.Lightbox })),
);
const VideoModal = lazy(() =>
  import("../internal-ui/video-modal").then((m) => ({ default: m.VideoModal })),
);

/** A venue's published reel, when it has one (the same rule as the app). */
export interface ActivityGalleryVideo {
  url: string;
  poster?: string;
}

/** Where the venue is: adds "See the map" on the desktop photos. */
export interface ActivityGalleryMap {
  latitude: number | string;
  longitude: number | string;
}

interface ActivityGalleryProps {
  images: string[];
  title: string;
  /**
   * The venue's reel: a "Watch video" button on the photos opens it full
   * screen. Nothing plays in the gallery itself (owner, 2026-10-15).
   */
  video?: ActivityGalleryVideo | null;
  /** Full screen's line under the name, e.g. "Marrakech · From 300 MAD". */
  videoMeta?: string | null;
  /** Full screen's short description, shown beside the video on desktops. */
  videoDescription?: string | null;
  /** Full screen's button ("Book now", or "See details" when nothing books). */
  videoCta?: VideoModalCta | null;
  /**
   * The venue's pin. From `lg` up a "See the map" pill splits the frame in
   * two: the photos shrink to the start half, the map opens in the other.
   */
  map?: ActivityGalleryMap | null;
  /** Phones and tablets: the back button on the photo. */
  onBack?: () => void;
  /** Phones and tablets: the round buttons on the photo (heart, share). */
  phoneActions?: PhoneGalleryAction[];
}

/** The frame's outer radius. The photos inside sit at this less the band. */
const FRAME_RADIUS = 26;

/* The lit-metal image frame (the same one the activity cards wear), with the
   cards' soft lift under it. Image surfaces take the frame, never the pale
   reading band. */
function frameStyle(radius: number): CSSProperties {
  const frame = pewterFrame(radius);
  return {
    ...frame,
    boxShadow: `${frame.boxShadow}, 0 10px 30px rgba(4,0,51,0.10)`,
  } as CSSProperties;
}

/* A white control with one navy hairline, lifted a touch off the photo. */
const PILL_STYLE: CSSProperties = {
  background: "#fff",
  color: PW.navy,
  boxShadow: `0 0 0 1px ${PW.controlEdge}, 0 6px 16px -6px rgba(17,14,57,0.35)`,
};

/**
 * Where photo `index` sits in the desktop mosaic (a 4 x 2 grid) when `count`
 * photos are shown, at most five. One big photo on the start side and up to
 * four beside it; a shorter set fills the same box without an empty cell.
 */
export function mosaicCellSpan(count: number, index: number): { cols: number; rows: number } {
  if (count <= 1) return { cols: 4, rows: 2 };
  if (count === 2) return { cols: 2, rows: 2 };
  if (index === 0) return { cols: 2, rows: 2 };
  if (count === 3) return { cols: 2, rows: 1 };
  // Four photos: one wide on top, two below it.
  if (count === 4 && index === 1) return { cols: 2, rows: 1 };
  return { cols: 1, rows: 1 };
}

/* Start-side offsets (insetInlineStart), so Arabic mirrors the mosaic as the
   grid did. */
type Rect = { insetInlineStart: string; top: string; width: string; height: string };

/** The mosaic's gap, in px, between photos and between photos and map. */
const GAP = 3;

/* Column/row start of photo `index` in the 4 x 2 mosaic, matching the spans
   above as CSS grid would auto-place them. */
function mosaicCellStart(count: number, index: number): { col: number; row: number } {
  if (index === 0) return { col: 0, row: 0 };
  if (count === 2) return { col: 2, row: 0 };
  if (count === 3) return { col: 2, row: index - 1 };
  if (count === 4) return index === 1 ? { col: 2, row: 0 } : { col: index, row: 1 };
  return { col: 2 + ((index - 1) % 2), row: index <= 2 ? 0 : 1 };
}

/**
 * Where photo `index` sits with the map shut: the mosaic, as absolute boxes so
 * it can glide to the open layout (CSS grid cannot animate between the two).
 */
export function closedRect(count: number, index: number): Rect {
  const { cols, rows } = mosaicCellSpan(count, index);
  const { col, row } = mosaicCellStart(count, index);
  const colW = `(100% - ${3 * GAP}px) / 4`;
  const rowH = `(100% - ${GAP}px) / 2`;
  return {
    insetInlineStart: `calc(${colW} * ${col} + ${GAP * col}px)`,
    top: `calc(${rowH} * ${row} + ${GAP * row}px)`,
    width: `calc(${colW} * ${cols} + ${GAP * (cols - 1)}px)`,
    height: `calc(${rowH} * ${rows} + ${GAP * (rows - 1)}px)`,
  };
}

/** The big photo's share of the height with the map open; a strip takes the rest. */
const OPEN_HERO_SHARE = 78;
const HALF = `(50% - ${GAP / 2}px)`;

/**
 * Where photo `index` sits with the map open (owner pick "B", 2026-10-28):
 * the photos keep the start half, the big one on top and the rest as an
 * even strip under it; the map takes the other half.
 */
export function openRect(count: number, index: number): Rect {
  if (count <= 1) return { insetInlineStart: "0px", top: "0px", width: `calc${HALF}`, height: "100%" };
  if (index === 0) {
    return { insetInlineStart: "0px", top: "0px", width: `calc${HALF}`, height: `${OPEN_HERO_SHARE}%` };
  }
  const n = count - 1;
  const i = index - 1;
  const w = `(${HALF} - ${GAP * (n - 1)}px) / ${n}`;
  return {
    insetInlineStart: `calc(${w} * ${i} + ${GAP * i}px)`,
    top: `calc(${OPEN_HERO_SHARE}% + ${GAP}px)`,
    width: `calc(${w})`,
    height: `calc(${100 - OPEN_HERO_SHARE}% - ${GAP}px)`,
  };
}

/* One glide for every box; Tailwind's motion-reduce makes it instant. Each box
   names its own transitionProperty inline (logical insets have no Tailwind
   shorthand). */
const GLIDE = "duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:duration-0";
const PHOTO_GLIDE = "inset-inline-start, top, width, height";

/* The mosaic's boxes are inline style, not Tailwind classes: the city sites
   render this component and their stylesheets do not scan this package. */

const SRCSET_WIDTHS = [480, 720, 1080, 1440, 1920] as const;

const MOSAIC_HERO_SIZES = "(max-width: 1280px) 50vw, 640px";
/* Tailwind's `lg`, the line between the swipe and the mosaic: the page's
   own desktop layout (the booking sidebar) starts there too. */
const DESKTOP_MEDIA = "(min-width: 1024px)";
const PHONE_MEDIA = "(max-width: 1023.98px)";

/**
 * Both layouts are in the HTML and CSS hides one, so the first photo exists
 * twice. Left to itself React preloads every eager high-priority image, and
 * phones downloaded the hidden mosaic photo at full priority next to the one
 * they show (and desktops the hidden swipe photo), slowing LCP (Clarity,
 * 2026-10-10). Each copy is preloaded only for the screen that shows it, and
 * both <img>s are lazy: that stops React's own preload, a hidden lazy image is
 * never fetched, and the visible one finds its file already downloading.
 */
function preloadHero(src: string) {
  const url = src.trim();
  if (!url) return;
  const srcSet = imageSrcSet(url, SRCSET_WIDTHS);
  // A host we cannot resize serves both layouts the same file: one preload.
  if (!srcSet) {
    preload(url, { as: "image", fetchPriority: "high" });
    return;
  }
  for (const [sizes, media] of [
    [MOSAIC_HERO_SIZES, DESKTOP_MEDIA],
    ["100vw", PHONE_MEDIA],
  ] as const) {
    preload(url, { as: "image", fetchPriority: "high", imageSrcSet: srcSet, imageSizes: sizes, media });
  }
}

/**
 * The photos at the top of an activity page.
 *
 * From `lg` up: a mosaic in one pewter frame, one large photo and up to four
 * smaller ones, with an "All N photos" pill that opens the lightbox. Below
 * `lg` (phones and tablets): the app's gallery, see `PhoneGallery`.
 */
export function ActivityGallery({
  images,
  title,
  video,
  videoMeta,
  videoDescription,
  videoCta,
  map,
  onBack,
  phoneActions,
}: ActivityGalleryProps) {
  const { t } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  /* The map loads on the first open, then stays so reopening is instant. */
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMapOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mapOpen]);
  const list = (images ?? []).filter((src) => typeof src === "string" && src.trim().length > 0);

  const toggleMap = () => {
    setMapLoaded(true);
    setMapOpen((o) => !o);
  };

  if (list.length === 0 && !video && !onBack && !phoneActions?.length) return null;
  if (list.length > 0) preloadHero(list[0]);

  const grid = list.slice(0, 5);
  const count = grid.length;
  const allLabel = t("activity.all_photos", {
    count: list.length,
    defaultValue: "All {{count}} photos",
  });

  const pillClass =
    "inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 font-sans text-[13.5px] font-semibold";

  /* The pills on the mosaic: "See the map" when the venue has a pin, "Watch
     video" when it has a reel, then "All N photos" when there is more than
     one photo. */
  const photoPills = () => {
    const showAll = list.length > 1;
    if (!map && !video && !showAll) return null;
    return (
      <>
        {map && (
          <button
            type="button"
            onClick={toggleMap}
            /* Google's embed takes a few seconds: start it on the way to the click. */
            onPointerEnter={() => setMapLoaded(true)}
            onFocus={() => setMapLoaded(true)}
            aria-expanded={mapOpen}
            aria-controls="gallery-map"
            className={pillClass}
            style={mapOpen ? { ...PILL_STYLE, background: PW.navy, color: "#fff" } : PILL_STYLE}
            data-testid="gallery-map-toggle"
          >
            <Icon name="ri-map-2-line" className="text-[15px]" aria-hidden />
            {mapOpen ? t("activity.gallery_hide_map") : t("activity.gallery_show_map")}
          </button>
        )}
        {video && (
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            className={pillClass}
            style={PILL_STYLE}
            data-testid="watch-video"
          >
            <Icon name="ri-play-fill" className="text-[15px]" aria-hidden />
            {t("activity.watch_video")}
          </button>
        )}
        {showAll && (
          <button
            type="button"
            onClick={() => setLightboxIndex(0)}
            className={pillClass}
            style={PILL_STYLE}
            data-testid="all-photos"
          >
            <Icon name="ri-layout-grid-line" className="text-[15px]" aria-hidden />
            {allLabel}
          </button>
        )}
      </>
    );
  };

  return (
    <>
      {/* Desktop: the mosaic. */}
      {count > 0 && (
      <div
        className="hidden lg:block"
        style={frameStyle(FRAME_RADIUS)}
        data-testid="photo-mosaic"
        data-count={count}
      >
        <div
          className="relative overflow-hidden"
          style={{ borderRadius: FRAME_RADIUS - PEWTER_GAP, height: "clamp(360px, 38vw, 500px)" }}
        >
          <div style={{ position: "relative", height: "100%" }}>
            {grid.map((src, i) => {
              const span = mosaicCellSpan(count, i);
              const rect = mapOpen ? openRect(count, i) : closedRect(count, i);
              return (
              <button
                key={`${src}-${i}`}
                type="button"
                onClick={() => setLightboxIndex(i)}
                aria-label={t("activity.open_photo", {
                  index: i + 1,
                  count: list.length,
                  defaultValue: "Open photo {{index}} of {{count}}",
                })}
                className={`group absolute min-h-0 min-w-0 overflow-hidden ${GLIDE}`}
                style={{ ...rect, transitionProperty: PHOTO_GLIDE }}
                data-testid="mosaic-cell"
                data-cols={span.cols}
                data-rows={span.rows}
              >
                <ImageWithFallback
                  src={src}
                  alt={`${title} ${i + 1}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                  fetchPriority={i === 0 ? "high" : "auto"}
                  decoding={i === 0 ? "sync" : "async"}
                  sizes={
                    i === 0 || count <= 2
                      ? MOSAIC_HERO_SIZES
                      : "(max-width: 1280px) 25vw, 320px"
                  }
                  srcsetWidths={SRCSET_WIDTHS}
                />
              </button>
              );
            })}
            {map && (
              <div
                id="gallery-map"
                className={`absolute top-0 h-full overflow-hidden ${GLIDE}`}
                style={{
                  insetInlineStart: mapOpen ? `calc(50% + ${GAP / 2}px)` : "100%",
                  width: `calc${HALF}`,
                  opacity: mapOpen ? 1 : 0,
                  visibility: mapOpen ? "visible" : "hidden",
                  transitionProperty: "inset-inline-start, opacity, visibility",
                }}
                aria-hidden={!mapOpen}
                data-testid="gallery-map"
              >
                {mapLoaded && (
                  <LocationMap
                    title={t("activity.map")}
                    latitude={map.latitude}
                    longitude={map.longitude}
                    className="block h-full w-full border-0"
                  />
                )}
                <button
                  type="button"
                  onClick={() => setMapOpen(false)}
                  aria-label={t("activity.gallery_close_map")}
                  className="absolute end-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full"
                  style={PILL_STYLE}
                  tabIndex={mapOpen ? 0 : -1}
                >
                  <Icon name="ri-close-line" className="text-[18px]" aria-hidden />
                </button>
                <div className="absolute bottom-3 start-3">
                  <DirectionsLink latitude={map.latitude} longitude={map.longitude} />
                </div>
              </div>
            )}
          </div>
          {/* With the map open the pills ride on the big photo, above the strip. */}
          <div
            className={`absolute z-[1] flex items-center gap-2 ${GLIDE}`}
            style={
              mapOpen
                ? {
                    insetInlineEnd: `calc(50% + ${GAP / 2 + 12}px)`,
                    bottom: count > 1 ? `calc(${100 - OPEN_HERO_SHARE}% + 12px)` : "12px",
                    transitionProperty: "inset-inline-end, bottom",
                  }
                : { insetInlineEnd: "12px", bottom: "12px", transitionProperty: "inset-inline-end, bottom" }
            }
          >
            {photoPills()}
          </div>
        </div>
      </div>
      )}

      {/* Phones and tablets: the app's gallery (owner, 2026-10-15), one
          rounded card swiped photo by photo with the buttons on it. No pewter
          frame here (owner, 2026-09-29): the band eats width a phone photo
          cannot spare. */}
      <PhoneGallery
        className="lg:hidden"
        images={list}
        title={title}
        onOpenPhoto={setLightboxIndex}
        onVideo={video ? () => setVideoOpen(true) : undefined}
        fallbackImage={video?.poster ?? null}
        onBack={onBack}
        actions={phoneActions}
      />

      {videoOpen && video && (
        <Suspense fallback={null}>
          <VideoModal
            src={video.url}
            poster={video.poster ?? list[0]}
            title={title}
            meta={videoMeta}
            description={videoDescription}
            cta={videoCta}
            onClose={() => setVideoOpen(false)}
          />
        </Suspense>
      )}

      {lightboxIndex !== null && (
        <Suspense fallback={null}>
          <Lightbox
            images={list}
            activeIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />
        </Suspense>
      )}
    </>
  );
}
